import { z } from 'zod'

// ─── Alert windows ───
//
// `/v2/alert-windows` rows (read once, to copy a pre-panel-state window into
// its panel), the panel's own window config, and the `alerts` rpc frames.

/** Postgres NUMERIC can serialize as a string; accept both. */
const num = z.preprocess(
  (v) => (typeof v === 'string' && v.trim() !== '' ? Number(v) : v),
  z.number().finite(),
)
const optStr = z.string().nullable().default(null)

/** One alert of a window: the reference engine's `criteria.Alert` JSON. Unknown keys are kept so an edit round-trips. */
export const AlertSpecSchema = z
  .object({
    trigger: z.string().optional(),
    field: z.string().optional(),
    value: z.union([z.number(), z.string()]).optional(),
    timeframe: z.string().optional(),
    scope: z.string().optional(),
    level: z.string().optional(),
    expression: z.string().optional(),
    cooldown_seconds: z.number().optional(),
    rearm_seconds: z.number().optional(),
    max_per_day: z.number().optional(),
    fire_while_true: z.boolean().optional(),
    confirm: z
      .object({ hold_seconds: z.number().optional(), min_volume: z.number().optional() })
      .passthrough()
      .optional(),
  })
  .passthrough()
export type AlertSpec = z.infer<typeof AlertSpecSchema>

export const AlertUniverseSchema = z
  .object({
    symbols: z.array(z.string()).optional(),
    watchlist_ids: z.array(z.number()).optional(),
  })
  .passthrough()
export type AlertUniverse = z.infer<typeof AlertUniverseSchema>

export const AlertWindowSchema = z.object({
  window_id: z.string(),
  name: z.string(),
  profile_id: z.string(),
  profile_page: optStr,
  profile_type: optStr,
  alerts: z.array(AlertSpecSchema).default([]),
  universe: AlertUniverseSchema.nullable().default(null),
  notify: z.boolean().default(false),
  is_active: z.boolean(),
  version: z.number().default(1),
  archived_at: optStr,
  created_at: z.string(),
  updated_at: z.string(),
})
export type AlertWindow = z.infer<typeof AlertWindowSchema>

export const AlertWindowListSchema = z.object({ windows: z.array(AlertWindowSchema) })

/** One Alert Window panel's window, kept in its panel state (the window id is the panel's). */
export const AlertWindowConfigSchema = z.object({
  name: z.string(),
  alerts: z.array(AlertSpecSchema),
  symbols: z.array(z.string()).optional(),
  notify: z.boolean().optional(),
  paused: z.boolean().optional(),
})
export type AlertWindowConfig = z.infer<typeof AlertWindowConfigSchema>

// ─── `alerts` rpc service frames (apps/edge-streaming/internal/alertrpc/PROTOCOL.md) ───

export const AlertWindowFireSchema = z.object({
  window: z.string(),
  kind: z.literal('fire'),
  hash: z.string(),
  event_id: z.string(),
  symbol: z.string(),
  price: num,
  bar_ts: z.number(),
  trading_date: z.string(),
  alert_index: z.number().int().nonnegative(),
  trigger: z.string().default(''),
  label: z.string().default(''),
  matched_at: z.string(),
})
export type AlertWindowFire = z.infer<typeof AlertWindowFireSchema>

export const AlertWindowFrameSchema = z.discriminatedUnion('kind', [
  z.object({
    window: z.string(),
    kind: z.literal('opened'),
    hash: z.string(),
    seeded: z.number().optional(),
  }),
  AlertWindowFireSchema,
  z.object({
    window: z.string(),
    kind: z.literal('error'),
    code: z.string(),
    message: z.string().optional(),
  }),
])
export type AlertWindowFrame = z.infer<typeof AlertWindowFrameSchema>
